<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', variant: 'success', callback: confirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div v-if="chargeModel" class="pt-4 pb-4 -mx-4 mt-2 px-4 text-left border-t border-b bg-gray-50 dark:bg-gray-900 max-h-[60vh] overflow-y-auto">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('general.title')" class="w-full mb-6 block" rules="required">
          <text-field v-model="chargeModel.title" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.price')"
          :name="$t('general.price')"
          class="mb-6 block"
          :rules="isAmountTypePercentage ? 'required|between:0,100' : 'required|currency'"
        >
          <div class="flex items-center space-x-2">
            <currency-field v-if="isAmountTypeFixed" v-model="_price" :status="status" class="w-1/2" />
            <number-field
              v-if="isAmountTypePercentage"
              v-model="_price"
              :min="0"
              :max="100"
              :status="status"
              trailing="%"
              class="w-1/2"
              slim
            />
            <segment-field v-model="chargeModel.amountType" :options="chargeAmountTypeOptions" class="border w-1/2" />
          </div>
        </o-field-group>

        <o-field-group :label="$t('general.loyaltyEnabled')" :info="$t('general.loyaltyEnabledInfo')" class="w-full mb-6 block">
          <toggle-field v-model="chargeModel.loyaltyEnabled" />
        </o-field-group>

        <o-field-group :label="$t('general.locked')" :info="$t('components.chargeModel.actions.edit.lockedInfo')" class="block w-full mb-6">
          <toggle-field v-model="chargeModel.locked" />
        </o-field-group>

        <tax-select v-model="chargeModel.taxIDs" class="w-full mb-2" />
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import TaxSelect from '@/components/tax/elements/Select.vue'
import { OFieldGroup } from '@/components/elements'
import { TextField, CurrencyField, NumberField, ToggleField, SegmentField } from '@/components/fields'

import { ChargeAmountType, ChargeAmountTypeLabels } from '~/_api/models/chargeModel'
import type { ChargeModel } from '~/_api/models/chargeModel'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success', chargeModel: ChargeModel): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const chargeModel = ref<ChargeModel | null>(null)

const title = computed(() => {
  if (chargeModel.value && isNewRecord.value) {
    return $i18n.t('components.chargeModel.actions.edit.new')
  }

  return $i18n.t('components.chargeModel.actions.edit.edit')
})

const _price = computed({
  get () {
    return chargeModel.value?.price
  },
  set (val) {
    if (chargeModel.value) {
      chargeModel.value.price = Number(val)
    }
  },
})

const isNewRecord = computed(() => {
  return chargeModel.value?.isNewRecord ?? true
})

const isAmountTypeFixed = computed(() => {
  return chargeModel.value?.isAmountTypeFixed ?? false
})

const isAmountTypePercentage = computed(() => {
  return chargeModel.value?.isAmountTypePercentage ?? false
})

const chargeAmountTypeOptions = computed(() => {
  return Object.values(ChargeAmountType)
    .filter((v) => typeof v === 'number')
    .map((item) => {
      return {
        label: $i18n.t(ChargeAmountTypeLabels[item as ChargeAmountType]),
        value: item,
      }
    })
})

const open = async (model?: ChargeModel) => {
  try {
    if (model) {
      chargeModel.value = model
    } else {
      const response = await $api.chargeModel.defaults(null as any)
      chargeModel.value = response.data
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  dialog.value.open()
}

const confirm = async () => {
  if (!chargeModel.value) return

  emit('update:loading', true)

  try {
    const response = await $api.chargeModel.save(chargeModel.value)
    emit('update:loading', false)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('components.chargeModel.actions.edit.success'),
        text: $i18n.t('components.chargeModel.actions.edit.successInfo', { title: chargeModel.value.title }),
        variant: 'success',
      })

      emit('success', chargeModel.value)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

const validate = () => {
  return form.value.validate()
}

defineExpose({ open })
</script>
